import { Injectable } from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { TranslateService } from '@ngx-translate/core';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { NgxPermissionsService } from 'ngx-permissions';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import Swal, { SweetAlertOptions } from 'sweetalert2';

import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { DialogConfig, ShipstationApiService } from 'v2/modules/shared';
import { IAgGridDropdownMenuOption } from 'v2/modules/shared/components/ag-grid-dropdown-menu-cell-renderer/ag-grid-dropdown-menu-cell-renderer.component';
import { IGridActionService } from 'v2/modules/shared/interfaces/ag-grid/ag-grid.types';
import { EShipmentFormDataSource } from 'v2/modules/shared/interfaces/order-shipment.interface';
import { BaseGridActionService } from 'v2/modules/shared/services/ag-grid/base-grid-action.service';
import { ShipstationShipmentApiService } from 'v2/modules/shared/services/api/shipstation-shipment-api.service';
import { UiConfirmationDialogComponent } from 'v2/modules/ui';

import { OrderShipmentsDetailsComponent } from '../../order-shipments-details/order-shipments-details.component';
import { OrderShipmentsFormComponent } from '../../order-shipments-form/order-shipments-form.component';

@Injectable({
  providedIn: 'root',
})
export class ShipmentsGridActionService
  extends BaseGridActionService
  implements IGridActionService
{
  @BlockUI() blockUI :NgBlockUI;

  constructor(
    private translate :TranslateService,
    private toastr :ToastrService,
    private shipstationApiService :ShipstationApiService,
    private dialog :MatLegacyDialog,
    private shipstationShipmentService :ShipstationShipmentApiService,
    featureToggleService :FeatureToggleService,
    permissionsService :NgxPermissionsService,
  ) {
    super(featureToggleService, permissionsService);
  }

  getDropdownActions() :IAgGridDropdownMenuOption[] {
    const defaultActions :IAgGridDropdownMenuOption[] = [
      {
        label: 'Create Invoice From Shipment',
        icon: '',
        onRowClick: (row) => {
          this.createInvoiceFromShipment(row?.id);
        },
      },
      {
        label: 'View Shipment',
        icon: '',
        onRowClick: (row) => {
          this.viewShipment(row?.id);
        },
      },
      {
        label: 'Update Shipment',
        icon: '',
        visible: (row) => !row?.appIntegrationAccountId && !row?.salesOrderShipmentId,
        onRowClick: (row) => {
          this.updateShipment(row?.id);
        },
      },
      {
        label: 'Delete Shipment',
        icon: '',
        visible: (row) => !row?.appIntegrationAccountId,
        onRowClick: (row) => {
          this.deleteShipment(row?.id);
        },
      },
    ];

    return this.getFilteredDropdownOptions(defaultActions);
  }

  private createInvoiceFromShipment(shipmentId :number) :void {
    const swalTitle = 'Create Invoice From Shipment';
    const swalType = 'warning';
    const swalOptions :SweetAlertOptions = {
      title: swalTitle,
      text: `Do you want to create invoice from this shipment?`,
      type: swalType,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
    };

    Swal.fire(swalOptions).then((result) => {
      if (!result.dismiss) {
        this.shipstationApiService.createInvoiceFromShipment(shipmentId).subscribe(
          (res :any) => {
            this.toastr.success(res.success);
          },
          (error) => {
            this.toastr.error(
              error?.error ? error.error : 'Error. Please try again later.',
            );
          },
        );
      }
    });
  }

  private updateShipment(shipmentId :number) :void {
    this.dialog
      .open(OrderShipmentsFormComponent, {
        width: DialogConfig.Size.LARGE,
        data: {
          source: EShipmentFormDataSource.SHIPMENT,
          sourceId: shipmentId,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.refreshGrid();
        }
      });
  }

  private viewShipment(shipmentId :number) :void {
    this.dialog.open(OrderShipmentsDetailsComponent, {
      width: DialogConfig.Size.LARGE,
      data: {
        id: shipmentId,
      },
    });
  }

  private deleteShipment(shipmentId :number) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Delete Shipment',
          message: 'Are you sure you want to delete this shipment?',
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirm) => {
        if (!confirm) {
          return;
        }

        this.shipstationShipmentService.deleteShipment(shipmentId).subscribe({
          next: () => {
            this.toastr.success('Shipment deleted successfully');
            this.refreshGrid();
          },
          error: (e) => {
            this.toastr.error(e?.error || 'Failed to delete shipment');
          },
        });
      });
  }
}
